// src/app/modules/ThreatAlerts/pages/ThreatAlerts.jsx
import React, { useEffect, useState, useRef } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import * as actions from "../../Dashbord/_redux/dashboard/Actions";
import { ThreatAlertsTable } from "../database";
import { ConfirmActionModal } from '../../../components/ConfirmActionModal';
import {
  Shield, AlertTriangle, Search, Filter,
  CheckCircle, XCircle, Activity,
} from 'lucide-react';
import './threat-alerts.scss';

export function ThreatAlerts() {
  const dispatch = useDispatch();
  const isMountedRef = useRef(true);

  const { org, alerts, alertsMeta, loading } = useSelector(
    (state) => ({
      org:       state.auth.organization,
      alerts:    state.dashboard.threat_alerts,
      alertsMeta: state.dashboard.threat_alerts_meta,
      loading:   state.dashboard.listLoading,
    }),
    shallowEqual
  );

  const [currentPage,      setCurrentPage]      = useState(1);
  const [perPage]                               = useState(10);
  const [severityFilter,   setSeverityFilter]   = useState('');
  const [statusFilter,     setStatusFilter]     = useState('');
  const [alertTypeFilter,  setAlertTypeFilter]  = useState('');

  const [showAcknowledgeModal, setShowAcknowledgeModal] = useState(false);
  const [showResolveModal,     setShowResolveModal]     = useState(false);
  const [selectedAlert,        setSelectedAlert]        = useState(null);
  const [actionLoading,        setActionLoading]        = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (org?.organization_id) fetchAlerts();
  }, [currentPage, severityFilter, statusFilter, alertTypeFilter]);

  function fetchAlerts() {
    if (!isMountedRef.current) return;
    const params = { page: currentPage, limit: perPage, organization_id: org.organization_id };
    if (severityFilter)  params.severity   = severityFilter;
    if (statusFilter)    params.status     = statusFilter;
    if (alertTypeFilter) params.alert_type = alertTypeFilter;
    dispatch(actions.fetchAllData('threat_alerts', org.organization_id, params));
  }

  function handleAcknowledge(alert) { setSelectedAlert(alert); setShowAcknowledgeModal(true); }
  function handleResolve(alert)     { setSelectedAlert(alert); setShowResolveModal(true); }

  async function handleAcknowledgeConfirm() {
    setActionLoading(true);
    try {
      await dispatch(actions.addData('acknowledge_alert', { alert_id: selectedAlert.alert_id }, org?.organization_id));
      fetchAlerts();
      setShowAcknowledgeModal(false);
    } catch (e) { console.error(e); }
    finally { setActionLoading(false); }
  }

  async function handleResolveConfirm() {
    setActionLoading(true);
    try {
      await dispatch(actions.addData('resolve_alert', { alert_id: selectedAlert.alert_id, status: 'resolved' }, org?.organization_id));
      fetchAlerts();
      setShowResolveModal(false);
    } catch (e) { console.error(e); }
    finally { setActionLoading(false); }
  }

  const stats = [
    {
      icon: <AlertTriangle size={16} />, bg: '#FEF2F2', color: '#DC2626',
      label: 'Active Alerts',
      value: alerts?.filter(a => a.status === 'active').length || 0,
    },
    {
      icon: <Shield size={16} />, bg: '#FFFBEB', color: '#D97706',
      label: 'Critical',
      value: alerts?.filter(a => a.severity === 'critical').length || 0,
    },
    {
      icon: <CheckCircle size={16} />, bg: '#F0FDF4', color: '#16a34a',
      label: 'Resolved',
      value: alerts?.filter(a => a.status === 'resolved').length || 0,
    },
    {
      icon: <Activity size={16} />, bg: '#F0EEFF', color: '#5341C4',
      label: 'Total',
      value: alertsMeta?.totalResults || 0,
    },
  ];

  return (
    <>
      <div className="threat-alerts-page">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="ac-header">
          <div className="ac-header__left">
            <h1>Threat Alerts</h1>
            <p>Monitor and respond to security threats</p>
          </div>
        </div>

        {/* ── Stats strip ─────────────────────────────────────────────────── */}
        <div className="ac-card">
          <div className="ac-code-stats" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            {stats.map((s, i) => (
              <div key={i} className="ac-code-stat">
                <div className="ac-code-stat__icon" style={{ background: s.bg, color: s.color }}>
                  {s.icon}
                </div>
                <div>
                  <div className="ac-code-stat__value">{s.value}</div>
                  <div className="ac-code-stat__label">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Filters ─────────────────────────────────────────────────────── */}
        <div className="ac-card">
          <div className="ac-card__body" style={{ padding: '11px 16px' }}>
            <div className="ac-filters">

              <div className="ac-search">
                <Search size={14} />
                <input placeholder="Search alerts…" />
              </div>

              <select
                className="ac-select"
                value={severityFilter}
                onChange={(e) => { setSeverityFilter(e.target.value); setCurrentPage(1); }}
              >
                <option value="">All Severities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>

              <select
                className="ac-select"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="resolved">Resolved</option>
                <option value="false_positive">False Positive</option>
              </select>

              <select
                className="ac-select"
                value={alertTypeFilter}
                onChange={(e) => { setAlertTypeFilter(e.target.value); setCurrentPage(1); }}
              >
                <option value="">All Types</option>
                <option value="blacklisted_visitor">Blacklisted Visitor</option>
                <option value="high_risk_visitor">High Risk Visitor</option>
                <option value="suspicious_pattern">Suspicious Pattern</option>
                <option value="network_threat">Network Threat</option>
              </select>

              <button
                className="ac-btn ac-btn--secondary"
                onClick={() => { setSeverityFilter(''); setStatusFilter(''); setAlertTypeFilter(''); }}
              >
                <XCircle size={13} />Reset
              </button>

              <button
                className="ac-apply-btn"
                onClick={() => { setCurrentPage(1); fetchAlerts(); }}
              >
                <Filter size={13} />Apply
              </button>

            </div>
          </div>
        </div>

        {/* ── Table ───────────────────────────────────────────────────────── */}
        <div className="ac-card">
          <div className="ac-card__header">
            <div className="ac-card__title">
              <Shield size={14} />Threat Alert Log
            </div>
            <span className="ac-card__meta">
              {alertsMeta?.totalResults || 0} total alerts
            </span>
          </div>
          <div className="ac-table-wrap">
            <ThreatAlertsTable
              alerts={alerts || []}
              loading={loading}
              onAcknowledge={handleAcknowledge}
              onResolve={handleResolve}
              currentPage={currentPage}
              perPage={perPage}
              totalCount={alertsMeta?.totalResults || 0}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>

      </div>

      <ConfirmActionModal
        show={showAcknowledgeModal}
        onHide={() => setShowAcknowledgeModal(false)}
        onConfirm={handleAcknowledgeConfirm}
        title="Acknowledge Alert"
        message={<div><p>Mark this alert as acknowledged?</p><p className="mt-2 text-muted">{selectedAlert?.description}</p></div>}
        confirmText="Acknowledge"
        type="info"
        loading={actionLoading}
      />

      <ConfirmActionModal
        show={showResolveModal}
        onHide={() => setShowResolveModal(false)}
        onConfirm={handleResolveConfirm}
        title="Resolve Alert"
        message={<div><p>Mark this alert as resolved?</p><p className="mt-2 text-muted">{selectedAlert?.description}</p></div>}
        confirmText="Resolve"
        type="success"
        loading={actionLoading}
      />
    </>
  );
}